<template>
  <v-dialog
    :value="showDialogBtn"
    width="800"
    persistent
    scrollable
  >
    <v-card>
      <AdCardTitle dialog>{{ selectedAssociation ? $t('edit-code') :  $t('add-coded-concept') }}</AdCardTitle>
      <v-card-text class="pa-5" v-if="!checkAssociationTypeSupport || associationTypesSupport.includes(activeAssociationType)">
        <v-form ref="associationRef" v-if="showDialogBtn">
          <EditableString
            v-model="association.displayName"
            ref="displayName"
            :rules="rulesAssociation.displayName"
            :required="rulesAssociation.requiredAttributes.includes('displayName')"
            :label="$t('display-name')"
          />
          <EditableString
            v-model="association.code"
            ref="code"
            :rules="rulesAssociation.code"
            :required="rulesAssociation.requiredAttributes.includes('code')"
            :label="$t('code')"
            hint="pattern (some manual text the user has entered)"
            show-dialog
            :disabled="!!selectedAssociation"
          />

          <v-combobox
            ref="codeSystem"
            class="required"
            :rules="codeSystemComboboxRules"
            :value="association.codeSystem"
            @input="handleCodeSystemInput"
            :label="$t('codesystem')"
            :items="codeSystemComboboxItems"
            :loading="$store.state.terminology.loading.codeSystemItems"
            :disabled="!!selectedAssociation"
            item-value="uuid"
            :filter="codeSystemSearchFilter"
            clearable
            @keydown.enter="blurCodeSystemCombobox"
          >
            <template #item="{ item }">
              <AdStatusDot larger class="mr-3" :status="item.statusCode" icon="mdi-format-list-bulleted-type" />
              {{ item.displayName || item.name }} {{(item.versionLabel)}}
            </template>
            <template #selection="{ item }">
              {{ item.id }}
              <v-chip v-if="item.displayName || item.name" class="ml-2" small outlined :disabled="!!selectedAssociation">
                <AdStatusDot class="mr-2" :status="item.statusCode" icon="mdi-format-list-bulleted-type" />
                {{ item.displayName || item.name }} {{(item.versionLabel)}}
              </v-chip>
            </template>
          </v-combobox>
          <slot name="additionalFormItems" :item="association">
            <v-select
              v-model="association.equivalence"
              item-value="value"
              item-text="#text"
              :items="equivalencyTypes"
              :label="$t('association-equivalence')"
              clearable
            />
          </slot>

        </v-form>
      </v-card-text>
      <AdInfoOwl v-else warning class="ma-5">
        {{$t('notyetimplemented')}}
      </AdInfoOwl>
      <v-card-actions class="dialogbottomstrip">
        <v-spacer></v-spacer>
        <AdButton type="cancel" @click="$emit('cancelAddAssociation', false)" />
        <AdButton
          v-if="!checkAssociationTypeSupport || associationTypesSupport.includes(activeAssociationType)"
          :type="selectedAssociation ? 'save' : 'add'"
          @click="addAssociation"
          depressed
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import modelAssociation from '@/models/association'
import rulesAssociation from '@/rules/association'
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'

/**
 * General component to add an Association to a source
 * Source is listed in associationTypes in parent-component
 */

export default {
  name: 'AdAssociationAdd',
  props: {
    associationTypesSupport: {
      type: Array,
      default: () => []
    },
    checkAssociationTypeSupport: {
      type: Boolean,
      default: true
    },
    showDialogBtn: {
      type: Boolean,
      default: false
    },
    selectedAssociation: {
      type: Object,
      required: false,
      default: null
    }
  },
  data () {
    return {
      association: null,
      rulesAssociation
    }
  },
  computed: {
    ...mapGetters({
      activeAssociationType: ['dataset/activeAssociationType'],
      getDecorTypes: ['server/getDecorTypes']
    }),
    codeSystemComboboxItems () {
      const csItems = this.$store.getters['terminology/codeSystemItems'] || []
      return [
        { header: this.$t('select-codesystem-or-enter-oid') },
        ...csItems
      ]
    },
    codeSystemComboboxRules () {
      return [
        v => !!v || this.$t('required-field'),
        v => /^[0-2](\.(0|[1-9][0-9]*))*$/.test(v?.id) || this.$t('invalid-input')
      ]
    },
    decorTypes () {
      return this.getDecorTypes?.data
    },
    equivalencyTypes () {
      return this.decorTypes?.EquivalencyType?.[0]?.enumeration?.map((e) => ({
        '#text': this.$filters.getMatchingTranslation({ value: e.label, locale: this.$i18n.locale }),
        value: e.value
      })
      ) || []
    }
  },
  methods: {
    blurCodeSystemCombobox () {
      this.$refs.codeSystem.blur()
    },
    addAssociation () {
      if (this.$refs.associationRef.validate()) {
        const payload = cloneDeep(this.association)
        payload.codeSystem = payload.codeSystem.id
        for (const prop in payload) {
          if ([null, ''].includes(payload[prop])) {
            delete payload[prop]
          }
        }
        this.$emit('addAssociation', payload)
      } else {
        // set the focus on the field that has the error
        const hasError = rulesAssociation.requiredAttributes.filter(attr => this.$refs[attr]?.hasError)
        if (hasError && hasError[0] && this.$refs[hasError[0]]) {
          this.$refs[hasError[0]].focus()
        }
      }
    },
    codeSystemSearchFilter (item, queryText) {
      const { displayName, name, id } = item
      return [displayName, name, id]
        .map(text => text?.toLowerCase())
        .some(text => text?.includes(queryText.toLowerCase()))
    },
    handleCodeSystemInput (value) {
      if (typeof value === 'string') {
        this.association.codeSystem = {
          id: value
        }
      } else if (typeof value === 'object') {
        this.association.codeSystem = value
      }
    }
  },
  watch: {
    showDialogBtn: {
      immediate: true,
      handler (dialogIsOpen) {
        if (dialogIsOpen) {
          // fetch codesystems if not loaded yet
          if (!this.$store.state.terminology.loaded.codeSystemItems) {
            this.$store.dispatch('terminology/getCodeSystemList', { prefix: this.projectInFocus?.prefix })
              .catch(error => {
                const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
                this.$oops(errorMessage)
              })
          }

          let association

          if (this.selectedAssociation) {
            association = cloneDeep(this.selectedAssociation)
            const codeSystem = this.$store.getters['terminology/codeSystemItems']?.find(cs => cs.id === association.codeSystem)
            association.codeSystem = codeSystem || { id: association.codeSystem }
          } else {
            association = cloneDeep(modelAssociation)
          }
          this.association = association
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .required .v-label:after {
  content: "*";
  font-weight: bold;
  color: var(--v-error-base);
}
</style>
